import { useState, useCallback } from 'react';
import { inventoryForecastApi } from '../api/inventoryForecast';
import type { InventoryForecast, GenerateInventoryForecastPayload } from '../api/inventoryForecast';

export function useInventoryForecast() {
  const [forecasts, setForecasts] = useState<InventoryForecast[]>([]);
  const [activeForecast, setActiveForecast] = useState<InventoryForecast | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchForecasts = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await inventoryForecastApi.list(p);
      setForecasts(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch {
      setError('Failed to load inventory forecasts.');
    } finally {
      setLoading(false);
    }
  }, []);

  const generate = useCallback(async (payload: GenerateInventoryForecastPayload = {}) => {
    setGenerating(true);
    setError(null);
    try {
      const res = await inventoryForecastApi.generate(payload);
      const forecast = res.data.data;
      setActiveForecast(forecast);
      setForecasts((prev) => [forecast, ...prev]);
      return forecast;
    } catch (err: any) {
      const msg = err?.message ?? 'Inventory forecast generation failed.';
      setError(msg);
      return null;
    } finally {
      setGenerating(false);
    }
  }, []);

  const deleteForecast = useCallback(async (id: number) => {
    try {
      await inventoryForecastApi.destroy(id);
      setForecasts((prev) => prev.filter((f) => f.id !== id));
      if (activeForecast?.id === id) setActiveForecast(null);
    } catch {
      setError('Failed to delete forecast.');
    }
  }, [activeForecast]);

  return {
    forecasts, activeForecast, loading, generating, error, total, page, lastPage,
    fetchForecasts, generate, deleteForecast, setActiveForecast, setError,
  };
}
